import { useEffect, useState } from 'react'
import { Feature } from '../../core/client/features.gen'
import { useFeature } from '../../core/features/FeaturesProvider'
import { allowedFileTypes } from '../../utils/documentUtils'

interface UseFileDragAndDropOptions {
  enabled: boolean
  onDropFiles: (files: File[]) => void
}

interface UseFileDragAndDropResult {
  isDraggingFile: boolean
  isDraggingUnsupportedFile: boolean
}

/**
 * Listens on the window for files dragged in from outside the page and
 * reports whether such a drag is currently in progress, invoking `onDropFiles`
 * with every dropped file. Drags that start inside the page are ignored.
 * `onDropFiles` must be referentially stable (wrap it in useCallback),
 * otherwise the listeners re-attach on every render.
 */
export function useFileDragAndDrop({ enabled, onDropFiles }: UseFileDragAndDropOptions): UseFileDragAndDropResult {
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isDraggingUnsupportedFile, setIsDraggingUnsupportedFile] = useState(false)
  const imagesEnabled = useFeature(Feature.IMAGE_INSPECTION)

  useEffect(() => {
    if (!enabled) {
      return
    }

    // The browser fires dragenter/dragleave for every element the cursor
    // crosses, including nested children. Counting enter/leave pairs lets us
    // distinguish actually leaving the window from merely moving between
    // adjacent elements: a child's dragenter fires before its parent's
    // dragleave, so the depth never hits zero until the cursor exits entirely.
    let dragDepth = 0

    // dragstart only fires for drags that begin inside the page.
    let dragStartedInPage = false

    const handleDragStart = () => {
      dragStartedInPage = true
    }

    const handleDragEnd = () => {
      dragStartedInPage = false
    }

    const isExternalFileDrag = (event: DragEvent): boolean =>
      !dragStartedInPage && (event.dataTransfer?.types?.includes('Files') ?? false)

    const handleDragEnter = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return
      event.preventDefault()
      dragDepth += 1
      setIsDraggingFile(true)
      // dataTransfer.items exposes MIME types during drag (before the drop), so
      // we can flag unsupported types early to give immediate visual feedback.
      // An empty type is unknown, not unsupported: Chrome leaves it empty for an
      // image dragged from another page. validateFile checks it on the drop.
      const draggedFileTypes = Array.from(event.dataTransfer?.items ?? [])
        .filter(item => item.kind === 'file' && item.type)
        .map(item => item.type)
      // Red only when none of the files is accepted; the others still upload.
      setIsDraggingUnsupportedFile(
        draggedFileTypes.length > 0 &&
          draggedFileTypes.every(mimeType => !allowedFileTypes(imagesEnabled).includes(mimeType)),
      )
    }

    const handleDragOver = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return
      // Without preventDefault the browser rejects the drop and navigates to
      // the file instead of letting us handle it.
      event.preventDefault()
    }

    const handleDragLeave = (event: DragEvent) => {
      if (!isExternalFileDrag(event)) return
      event.preventDefault()
      dragDepth -= 1
      if (dragDepth <= 0) {
        dragDepth = 0
        setIsDraggingFile(false)
        setIsDraggingUnsupportedFile(false)
      }
    }

    const handleDrop = (event: DragEvent) => {
      if (dragStartedInPage) return
      event.preventDefault()
      dragDepth = 0
      setIsDraggingFile(false)
      setIsDraggingUnsupportedFile(false)
      const files = Array.from(event.dataTransfer?.files ?? [])
      if (files.length > 0) {
        onDropFiles(files)
      }
    }

    window.addEventListener('dragstart', handleDragStart)
    window.addEventListener('dragend', handleDragEnd)
    window.addEventListener('mousemove', handleDragEnd)
    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragstart', handleDragStart)
      window.removeEventListener('dragend', handleDragEnd)
      window.removeEventListener('mousemove', handleDragEnd)
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', handleDrop)
    }
  }, [enabled, onDropFiles, imagesEnabled])

  return { isDraggingFile, isDraggingUnsupportedFile }
}
